import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { MuiBox, MuiButtonBase } from 'theme/material-ui';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { IconSize, FontSize, FontWeight } from 'modules/styles/variables';
import Svg from 'components/UI/Svg/Svg';
import { Icon } from 'components/Icon';
import { Color } from 'modules/styles/colors';
import Typography from 'components/UI/Typography';
import Divider from 'components/UI/Divider';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import YesNoRadioGroup from 'components/PreRegister/YesNoRadioGroup';
import { headerWithJWT, handleError } from '../utils';
import { StyledAsterisk } from 'components/RequiredFieldsLegend';
import { Subtitle, TooltipModal, TooltipInside, RightDiv } from './styled';
import {
  CheckListItem,
  VisitDetail,
  PatientInfo,
  UpdateSkipPaymentFlagPayload,
  GuarantorInfo
} from 'store/preRegistration/types';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationVisitDetailSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationPatientInfoSelector,
  PreRegistrationPatientGuarantorInfo
} from 'store/preRegistration/selectors';
import {
  UPDATE_PRE_REGISTRATION_CHECKLIST,
  updateSkipPaymentFlag
} from 'store/preRegistration/actions';
import { SkipPaymentFlags } from 'lib/constants/preRegister';
import { UPDATE_CHECKLIST_GUARANTOR } from 'services/orchestration/mutations';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { errors } from '../constant';
import { PreRegHeader } from '../common';

interface Props {
  guarantorInfo: GuarantorInfo;
  step: CheckListItem;
  isLoading: boolean;
  visitDetail: VisitDetail;
  patientInfo: PatientInfo;
  currentUrl: string;
  referringUrl: string;
  updateFlag: (payload: UpdateSkipPaymentFlagPayload) => void;
}

export const GuarantorInformationComponent = ({
  guarantorInfo,
  visitDetail,
  patientInfo,
  step,
  isLoading,
  updateFlag,
  currentUrl,
  referringUrl
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [updateChecklist, { loading }] = useMutation(UPDATE_CHECKLIST_GUARANTOR);

  const [infoCorrect, setInfoCorrect] = useState(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { firstName, lastName, relationship } = guarantorInfo || {};

  const onRadioButtonChange = (event: React.FormEvent<HTMLInputElement>) => {
    setInfoCorrect(event.target.value);
  };

  const onConfirmButtonPress = async () => {
    try {
      if (step.complete) {
        handleError(errors.Complete, history);
      } else if (infoCorrect) {
        const fin = visitDetail.finNumber;
        const mrn = patientInfo?.id;

        const mutationPayload = {
          arType: visitDetail.arType,
          visitType: visitDetail.type,
          checkListStepId: step.id,
          facilityCode: visitDetail.facilityCode,
          fin,
          mrn,
          stepData: {
            status: infoCorrect === 'no' ? 'CHANGE' : 'COMPLETE'
          }
        };

        const res = await updateChecklist({
          variables: {
            payload: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            headers: headerWithJWT()
          }
        });

        if (res.data.updateChecklistGuarantorInfoStep.success) {
          dispatch({
            type: UPDATE_PRE_REGISTRATION_CHECKLIST,
            payload: res.data.updateChecklistGuarantorInfoStep.data
          });

          updateFlag({ key: SkipPaymentFlags.GUARANTOR_INFO_CHANGED, value: infoCorrect === 'no' });

          const amplitudeEventData: AmplitudeEventData = {
            currentUrl,
            referringUrl,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_location: visitDetail?.location?.name || '',
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_type: `Hospital Registration: ${step.title}`,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_additional_info: `Is this information correct (${
              infoCorrect === 'yes' ? 'Yes' : 'No'
            })`
          };
          analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailComplete, amplitudeEventData);

          history.push('/pre-reg/checklist');
        } else {
          handleError(errors[step.type], history);
        }
      }
    } catch (err) {
      handleError(errors[step.type], history);
    }
  };

  useEffect(() => {
    const { id } = location.state || {};
    if (!id) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const tooltipText =
    'The guarantor is the person financially responsible for the patient. If over 18, the guarantor is self and if under 18, it is the parent/legal guardian.';

  const handleOpenTooltip = () => {
    setIsTooltipOpen(true);
  };

  const handleCloseTooltip = () => {
    setIsTooltipOpen(false);
  };

  return (
    <MuiBox
      minHeight="100%"
      display="flex"
      flexDirection="column"
      data-testid="guarantor-information-component"
    >
      <SpinnerModal isLoading={isLoading || loading} />
      <PreRegHeader title="Guarantor" />

      <MuiBox p={2}>
        <Subtitle data-testid="guarantor-subtitle-label">
          Please confirm your guarantor information below.
        </Subtitle>
        <MuiButtonBase onClick={handleOpenTooltip}>
          <Svg
            name="InfoOutlined"
            set="material"
            size={IconSize.base}
            color={Color.primary}
            data-testid="guarantor-tooltip-image"
          />
        </MuiButtonBase>
      </MuiBox>
      <Divider />
      <TooltipModal open={isTooltipOpen} onClose={handleCloseTooltip}>
        <TooltipInside>
          <RightDiv>
            <Icon
              onClick={handleCloseTooltip}
              data-testid="closeModal"
              name="close"
              size={IconSize.small}
            />
          </RightDiv>
          <p>{tooltipText}</p>
        </TooltipInside>
      </TooltipModal>
      <MuiBox p={2}>
        <Typography
          fontSize={FontSize.medium}
          color={Color.grayHue2}
          data-testid="guarantor-name-label"
        >
          Name
        </Typography>
        <Typography
          fontSize={FontSize.medium}
          fontWeight={FontWeight.bold}
          data-testid="guarantor-name-value"
        >
          {`${firstName} ${lastName}`}
        </Typography>
        <Spacer size="small" />
        <Typography
          fontSize={FontSize.medium}
          color={Color.grayHue2}
          data-testid="guarantor-relationship-label"
        >
          Relationship
        </Typography>
        <Typography
          fontSize={FontSize.medium}
          fontWeight={FontWeight.bold}
          data-testid="guarantor-relationship-value"
        >
          {relationship || 'Self'}
        </Typography>
      </MuiBox>
      <Divider />
      <MuiBox p={2}>
        <Typography>
          Is this information correct? <StyledAsterisk />
        </Typography>
        <YesNoRadioGroup
          data-testid="yes-no-radio"
          name="guarantor-information"
          value={infoCorrect}
          onChange={onRadioButtonChange}
        />
      </MuiBox>
      {infoCorrect === 'no' ? (
        <MuiBox px={2}>
          <Typography
            fontSize={FontSize.medium}
            fontWeight={FontWeight.bold}
            data-testid="guarantor-info-message"
          >
            You will be asked to update this when you arrive at your visit.
          </Typography>
        </MuiBox>
      ) : null}
      <MuiBox p={2} display="flex" flexGrow={1} flexDirection="column" justifyContent="flex-end">
        <StyledButton
          data-testid="submit-button"
          onPress={onConfirmButtonPress}
          disabled={!infoCorrect}
          title={infoCorrect === 'no' ? 'ok' : 'confirm'}
        >
          {infoCorrect !== 'no' ? 'Confirm' : 'OK'}
        </StyledButton>
      </MuiBox>
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState, props: Props) => {
  const id = props.location?.state.id || '';
  return {
    guarantorInfo: PreRegistrationPatientGuarantorInfo(state),
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

const mapDispatchToProps = {
  updateFlag: updateSkipPaymentFlag
};

export default connect(mapStateToProps, mapDispatchToProps)(GuarantorInformationComponent);
